// 메시지/대화 저장·내보내기·렌더 헬퍼(TXT/MD/PDF/DOCX, 생성문서 카드). setSaveMenu만 주입받음.
import { chatAPI } from '../../../api/client';
import { stripDocMarkers } from '../../../utils/docMarkers';
import MessageContent from '../MessageContent';
import { COLORS } from '../../../utils/uiStyles';
import { alertDialog } from '../../../components/AppDialog';
import { BOT_NAME } from '../../../utils/brand';
import { kstToday } from '../../../utils/kstTime';   // 🔴 파일명 날짜는 KST(2026-10-03)

export default function useChatExport({ setSaveMenu }) {
  // ── 응답 파일 저장 ────────────────────────────────
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadDoc = async (fileId, filename) => {
    try {
      const r = await chatAPI.downloadGenerated(fileId);
      downloadBlob(r.data, filename);
    } catch {
      alertDialog('문서 다운로드에 실패했습니다.');
    }
  };

  // 메시지 본문 렌더(코드블록·인라인코드·생성문서 카드) → pages/chat/MessageContent
  const renderMessage = (rawContent) => (
    <MessageContent content={rawContent} onDownloadDoc={downloadDoc} />
  );

  const deriveTitle = (text) => {
    const first = (text || '').split('\n').find((l) => l.trim());
    return ((first || '응답').replace(/^#+\s*/, '').replace(/[\\/:*?"<>|]/g, '').trim().slice(0, 40)) || '응답';
  };
  const normalizeConversationTags = (tags) => {
    const rawTags = Array.isArray(tags) ? tags : String(tags || '').split(',');
    return rawTags.map((tag) => String(tag).trim().replace(/^#+/, '')).filter(Boolean);
  };
  const buildConversationExportHeader = (conversation) => {
    if (!conversation) return '';
    const parts = [];
    if (conversation.is_favorite) parts.push('★');
    const tags = normalizeConversationTags(conversation.tags).map((tag) => `#${tag}`);
    if (tags.length) parts.push(tags.join(' '));
    return parts.join(' ');
  };
  const withConversationExportHeader = (content, conversation) => {
    const header = buildConversationExportHeader(conversation);
    return header ? `${header}\n\n${content}` : content;
  };
  const resolveConversationExportInput = (messagesOrConversation, conversation) => {
    if (Array.isArray(messagesOrConversation)) {
      return { messages: messagesOrConversation, conversation };
    }
    return {
      messages: Array.isArray(messagesOrConversation?.messages) ? messagesOrConversation.messages : [],
      conversation: messagesOrConversation,
    };
  };
  const sourceLabel = (sourceType) => {
    if (sourceType === 'web') return '웹';
    if (sourceType === 'project_rag') return '프로젝트 문서';
    if (sourceType === 'rag') return '사내 문서';
    return sourceType || '출처';
  };
  const citationLocation = (citation) => {
    const parts = [];
    if (citation.page !== null && citation.page !== undefined) parts.push(`p.${citation.page}`);
    if (citation.sheet) parts.push(`sheet ${citation.sheet}`);
    if (citation.slide !== null && citation.slide !== undefined) parts.push(`slide ${citation.slide}`);
    if (citation.start !== null && citation.start !== undefined && citation.end !== null && citation.end !== undefined) {
      parts.push(`${citation.start}-${citation.end}`);
    }
    return parts.join(' · ');
  };
  const citationEntries = (citations) => (
    Array.isArray(citations) ? citations.filter((c) => (c?.cited_text || '').trim()) : []
  );
  const citationTitle = (citation) => (
    citation.title || citation.url || (citation.document_id ? `문서 ${citation.document_id}` : '출처')
  );
  const citationMeta = (citation) => {
    const parts = [sourceLabel(citation.source_type)];
    if (citation.url) parts.push(citation.url);
    if (citation.document_id !== null && citation.document_id !== undefined) parts.push(`document:${citation.document_id}`);
    if (citation.chunk_id !== null && citation.chunk_id !== undefined) parts.push(`chunk:${citation.chunk_id}`);
    const location = citationLocation(citation);
    if (location) parts.push(location);
    if (citation.partial) parts.push('partial');
    return parts.join(' · ');
  };
  const formatCitationsText = (citations) => {
    const rows = citationEntries(citations);
    if (!rows.length) return '';
    return `\n\n[출처]\n${rows.map((citation, idx) => {
      const title = citationTitle(citation);
      const meta = citationMeta(citation);
      return `- [${citation.sequence || idx + 1}] ${title}${meta ? ` (${meta})` : ''}\n  인용: ${citation.cited_text}`;
    }).join('\n')}`;
  };
  const formatCitationsMd = (citations) => {
    const rows = citationEntries(citations);
    if (!rows.length) return '';
    return `\n\n#### 출처\n${rows.map((citation, idx) => {
      const title = citationTitle(citation);
      const meta = citationMeta(citation);
      return `- [${citation.sequence || idx + 1}] **${title}**${meta ? ` (${meta})` : ''}\n  - 인용: ${citation.cited_text}`;
    }).join('\n')}`;
  };
  const formatMessageText = (message) => `${stripDocMarkers(message.content)}${formatCitationsText(message.citations)}`;
  const formatMessageMd = (message) => `${stripDocMarkers(message.content)}${formatCitationsMd(message.citations)}`;
  const savePdf = (content, title) => {
    const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>body{font-family:'Malgun Gothic',sans-serif;padding:40px;color:#1f2937;max-width:800px;margin:0 auto;line-height:1.7;font-size:14px}pre{white-space:pre-wrap;word-break:break-word;font-family:inherit}</style>
</head><body><pre>${esc(content)}</pre></body></html>`;
    const win = window.open('', '_blank');
    if (!win) { alertDialog('팝업 차단을 해제해 주세요.'); return; }
    win.document.write(html); win.document.close(); win.focus();
    setTimeout(() => win.print(), 300);
  };
  const saveMsg = async (content, fmt, conversation, citations = []) => {
    setSaveMenu(null);
    const title = deriveTitle(content);
    const stamp = kstToday();
    const plainContent = `${content}${formatCitationsText(citations)}`;
    const markdownContent = `${content}${formatCitationsMd(citations)}`;
    if (fmt === 'txt') downloadBlob(new Blob([withConversationExportHeader(plainContent, conversation)], { type: 'text/plain;charset=utf-8' }), `${title}_${stamp}.txt`);
    else if (fmt === 'md') downloadBlob(new Blob([withConversationExportHeader(markdownContent, conversation)], { type: 'text/markdown;charset=utf-8' }), `${title}_${stamp}.md`);
    else if (fmt === 'pdf') savePdf(plainContent, title);
    else if (fmt === 'docx') {
      try { const r = await chatAPI.exportDocx({ content: plainContent, title }); downloadBlob(r.data, `${title}_${stamp}.docx`); }
      catch { alertDialog('DOCX 저장에 실패했습니다.'); }
    }
  };

  // ── 대화 내보내기 (#18) ──────────────────────────
  const exportTxt = (messagesOrConversation, conversation) => {
    const { messages, conversation: exportConversation } = resolveConversationExportInput(messagesOrConversation, conversation);
    const divider = '\n\n' + '─'.repeat(40) + '\n\n';
    const body = messages.map(m =>
      `[${m.role === 'user' ? '나' : BOT_NAME}]\n${formatMessageText(m)}`
    ).join(divider);
    const blob = new Blob([withConversationExportHeader(body, exportConversation)], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `대화_${kstToday()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportMd = (messagesOrConversation, conversation) => {
    const { messages, conversation: exportConversation } = resolveConversationExportInput(messagesOrConversation, conversation);
    const divider = '\n\n---\n\n';
    const body = messages.map(m =>
      `### ${m.role === 'user' ? '나' : BOT_NAME}\n\n${formatMessageMd(m)}`
    ).join(divider);
    const blob = new Blob([withConversationExportHeader(body, exportConversation)], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `대화_${kstToday()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = (messages) => {
    const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const citationHtml = (m) => {
      const rows = citationEntries(m.citations);
      if (!rows.length) return '';
      return `<div class="sources"><div class="sources-title">출처</div>${rows.map((citation, idx) => (
        `<div class="source"><div>${esc(`[${citation.sequence || idx + 1}] ${citationTitle(citation)} (${citationMeta(citation)})`)}</div><blockquote>${esc(citation.cited_text)}</blockquote></div>`
      )).join('')}</div>`;
    };
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>대화 내보내기</title>
<style>
  body{font-family:sans-serif;padding:40px;color:#1f2937;max-width:800px;margin:0 auto}
  h2{color:${COLORS.primary};margin-bottom:4px}
  .date{color:#9ca3af;font-size:12px;margin-bottom:24px}
  .msg{margin-bottom:20px}
  .role{font-size:12px;font-weight:700;margin-bottom:4px;color:#6b7280}
  .user .role{color:${COLORS.primary}}
  .content{font-size:14px;line-height:1.7;white-space:pre-wrap;word-break:break-word}
  .sources{margin-top:10px;padding:10px 12px;border:1px solid #e5e7eb;background:#f8fafc}
  .sources-title{font-size:12px;font-weight:700;color:#374151;margin-bottom:6px}
  .source{font-size:12px;color:#4b5563;margin-top:6px}
  blockquote{margin:4px 0 0;padding-left:10px;border-left:3px solid #d1d5db;white-space:pre-wrap}
  hr{border:none;border-top:1px solid #e5e7eb;margin:16px 0}
</style></head><body>
<h2>아워박스 AI 게이트웨이</h2>
<div class="date">${new Date().toLocaleString('ko-KR')} 내보내기</div>
${messages.map(m => `<div class="msg ${m.role}"><div class="role">${m.role === 'user' ? '나' : BOT_NAME}</div><div class="content">${esc(stripDocMarkers(m.content))}</div>${citationHtml(m)}</div><hr>`).join('')}
</body></html>`;
    const win = window.open('', '_blank');
    if (!win) { alertDialog('팝업 차단을 해제해 주세요.'); return; }
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 300);
  };

  return { downloadBlob, downloadDoc, renderMessage, deriveTitle, savePdf, saveMsg, exportTxt, exportMd, exportPdf };
}
